import {
  forEach,
  isObject,
  mapKeys,
  mapValues,
  pickBy,
  snakeCase,
} from 'lodash/fp';
import { getHostedFieldSelector } from '@zapier/payment/src/components/CreditCardForm/HostedFields/utils';
import {
  CreditCardBrandCode,
  HostedFieldAdditionalData,
  HostedFieldProcessorInterface,
  HostedFieldToken,
  HostedFieldType,
  HostedFieldValue,
} from '@zapier/payment/src/types';

class ScriptLoadError extends Error {
  event: string;

  constructor(message: string, onErrorArgs: Record<string, unknown>) {
    super(message);
    this.event = JSON.stringify(onErrorArgs);
  }
}

function loadScript(url: string) {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = url;
    script.onload = resolve;
    script.onerror = (event, source, line, column, error) =>
      reject(
        new ScriptLoadError(`Failed loading script at ${url}`, {
          event,
          source,
          line,
          column,
          error,
        })
      );

    if (document.body) {
      document.body.appendChild(script);
    }
  });
}

const pickByDeep = (obj: object): object =>
  mapValues(
    val => (isObject(val) ? pickByDeep(val) : val),
    pickBy(Boolean, obj)
  );

const assertNever = (value: never, noThrow?: boolean): never => {
  if (noThrow) {
    return value;
  }

  throw new Error(
    `Unhandled discriminated union member: ${JSON.stringify(value)}`
  );
};

type Result<T, E = Error> = { ok: true; value: T } | { ok: false; error: E };

/** @internal */
export class StripeProcessor implements HostedFieldProcessorInterface {
  stripe: stripe.Stripe;
  elements: stripe.elements.Elements;
  static instance: StripeProcessor | undefined | null = null;
  static elementsRegistry: Map<HostedFieldType, any> = new Map();

  constructor(stripe: stripe.Stripe) {
    this.stripe = stripe;
    this.elements = stripe.elements({
      fonts: [
        {
          cssSrc: 'https://fonts.googleapis.com/css?family=Open+Sans',
        },
      ],
    });
  }

  async createFields(
    style: any,
    onChange: (b: HostedFieldType, a: HostedFieldValue) => void
  ) {
    if (
      StripeProcessor.elementsRegistry.has('number') &&
      StripeProcessor.elementsRegistry.has('expiry') &&
      StripeProcessor.elementsRegistry.has('cvc')
    ) {
      return;
    }

    const types = ['cardNumber', 'cardExpiry', 'cardCvc'] as const;
    const simpleTypes = {
      cardNumber: 'number',
      cardExpiry: 'expiry',
      cardCvc: 'cvc',
    } as const;

    forEach(type => {
      const element = this.elements.create(type, {
        style: {
          base: style,
        },
      });

      const simpleType = simpleTypes[type];

      StripeProcessor.elementsRegistry.set(simpleType, element);

      element.addEventListener('change', response => {
        onChange(simpleType, {
          isEmpty: Boolean(response?.empty),
          originalError: response?.error?.message ?? '',
          brand: (response?.brand as CreditCardBrandCode) ?? 'unknown',
        });
      });

      element.mount(getHostedFieldSelector(simpleType));
    }, types);
  }

  destroyFields() {
    if (StripeProcessor.elementsRegistry.size === 0) return;

    const types = ['number', 'expiry', 'cvc'] as const;

    forEach(type => {
      const element = StripeProcessor.elementsRegistry.get(type);

      if (element) {
        element.destroy();
        StripeProcessor.elementsRegistry.delete(type);
      }
    }, types);
  }

  async createToken(
    additionalData: HostedFieldAdditionalData
  ): Promise<HostedFieldToken> {
    const firstElement = StripeProcessor.elementsRegistry.values().next().value;
    const cleanData = pickBy(Boolean, additionalData);
    const data = mapKeys(snakeCase, cleanData);
    const { error, token } = await this.stripe.createToken(firstElement, data);

    if (error) {
      throw new Error(error.message);
    }

    return {
      // @ts-ignore - our bespoke token/details types are mismatched from Stripe's
      token: token.id,
      // @ts-ignore - our bespoke token/details types are mismatched from Stripe's
      details: token.card,
    };
  }

  async confirmCardSetupIntent(
    clientSecret: string,
    billingDetails?: stripe.BillingDetails
  ): Promise<Result<stripe.setupIntents.SetupIntent, stripe.Error>> {
    const response = await this.stripe.confirmCardSetup(clientSecret, {
      payment_method: {
        card: StripeProcessor.elementsRegistry.get('number'),
        billing_details: billingDetails,
      },
    });

    if (response.error) {
      return { ok: false, error: response.error } as const;
    }

    if (response.setupIntent) {
      return { ok: true, value: response.setupIntent } as const;
    }

    return assertNever(response as never, true);
  }

  static async create(apiKey: string): Promise<HostedFieldProcessorInterface> {
    if (!StripeProcessor.instance) {
      if (!window.Stripe) {
        await loadScript('https://js.stripe.com/v3/');
      }

      StripeProcessor.instance = new StripeProcessor(window.Stripe?.(apiKey));
    }

    return StripeProcessor.instance;
  }
}

export type StripeProcessorType = InstanceType<typeof StripeProcessor>;

export default StripeProcessor.create;
